/* eslint-disable*/
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    ClipboardList,
    HelpCircle,
    BookOpen,
    FileText,
    Plus,
    Zap,
    Upload as UploadIcon,
} from 'lucide-react';
import adminServices from '../../../services/adminServices';

const ContentCreatorDashboard = () => {
    const userId = useSelector((state) => state.userInfo && state.userInfo.id)
    const { t, i18n } = useTranslation('translation');
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchDashboardData();
    }, []);


    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await adminServices.getContentCreatorDashboard(userId);
            if (result?.data) {
                setDashboardData(result.data.data);
            }
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const getStatsConfig = () => {
        if (!dashboardData) {
            return [
                {
                    titleKey: 'My Assessments',
                    value: '0',
                    subtitle: '0 Active • 0 Draft',
                    icon: ClipboardList,
                    iconBg: 'bg-blue-100',
                    iconColor: 'text-blue-500',
                    id: 'stat-card-my-assessments',
                },
                {
                    titleKey: 'My Questions',
                    value: '0',
                    subtitle: '0 MCQ • 0 Numeric',
                    icon: HelpCircle,
                    iconBg: 'bg-purple-100',
                    iconColor: 'text-purple-500',
                    id: 'stat-card-my-questions',
                },
                {
                    titleKey: 'Total Courses',
                    value: '0',
                    subtitle: 'Course library',
                    icon: BookOpen,
                    iconBg: 'bg-orange-100',
                    iconColor: 'text-orange-500',
                    id: 'stat-card-total-courses',
                },
                {
                    titleKey: 'Created Courses',
                    value: '0',
                    subtitle: 'Published courses',
                    icon: FileText,
                    iconBg: 'bg-green-100',
                    iconColor: 'text-green-500',
                    id: 'stat-card-created-courses',
                },
            ];
        }

        const { stats } = dashboardData;
        return [
            {
                titleKey: 'My Assessments',
                value: stats.assessments.total.toString(),
                subtitle: `${stats.assessments.active} Active • ${stats.assessments.draft} Draft`,
                icon: ClipboardList,
                iconBg: 'bg-blue-100',
                iconColor: 'text-blue-500',
                id: 'stat-card-my-assessments',
            },
            {
                titleKey: 'My Questions',
                value: stats.questions.total.toString(),
                subtitle: `${stats.questions.mcq} MCQ • ${stats.questions.numeric} Numeric`,
                icon: HelpCircle,
                iconBg: 'bg-purple-100',
                iconColor: 'text-purple-500',
                id: 'stat-card-my-questions',
            },
            {
                titleKey: 'Total Courses',
                value: stats.courses.total.toString(),
                subtitle: 'Course library',
                icon: BookOpen,
                iconBg: 'bg-orange-100',
                iconColor: 'text-orange-500',
                id: 'stat-card-total-courses',
            },
            {
                titleKey: 'Created Courses',
                value: stats.courses.created.toString(),
                subtitle: 'Published courses',
                icon: FileText,
                iconBg: 'bg-green-100',
                iconColor: 'text-green-500',
                id: 'stat-card-created-courses',
            },
        ];
    };

    const quickActions = [
        {
            titleKey: 'Create Question',
            subtitleKey: 'Add to question bank',
            icon: Plus,
            bgColor: 'bg-gradient-to-r from-purple-500 to-purple-600',
            hoverColor: 'hover:from-purple-600 hover:to-purple-700',
            path: '/app/question-create',
            id: 'quick-action-create-question',
        },
        {
            titleKey: 'Linear Assessment',
            subtitleKey: 'Fixed question sequence',
            icon: FileText,
            bgColor: 'bg-gradient-to-r from-blue-500 to-blue-600',
            hoverColor: 'hover:from-blue-600 hover:to-blue-700',
            path: '/app/assessmentCreate?type=linear',
            id: 'quick-action-linear-assessment',
        },
        {
            titleKey: 'Adaptive Assessment',
            subtitleKey: 'With difficulty branching',
            icon: Zap,
            bgColor: 'bg-gradient-to-r from-pink-500 to-pink-600',
            hoverColor: 'hover:from-pink-600 hover:to-pink-700',
            path: '/app/assessmentCreate?type=adaptive',
            id: 'quick-action-adaptive-assessment',
        },
        {
            titleKey: 'Bulk Import',
            subtitleKey: 'Import from CSV',
            icon: UploadIcon,
            bgColor: 'bg-gradient-to-r from-orange-500 to-red-500',
            hoverColor: 'hover:from-orange-600 hover:to-red-600',
            path: '/app/bulkUpload',
            id: 'quick-action-bulk-import',
        },
    ];

    const navigateToSection = (path) => {
        navigate(path);
    };

    const stats = getStatsConfig();

    if (loading) {
        return (
            <div id="content-creator-dashboard-loading" className="min-h-screen bg-white p-4 mt-12 md:p-6 lg:p-8">
                <div className="mb-6 md:mb-8">
                    <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                        {t('Content Creator Dashboard')}
                    </h1>
                    <p className="text-xs md:text-sm text-gray-600">
                        {t('Manage your educational content and track performance')}
                    </p>
                </div>

                <div id="stats-skeleton-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} id={`stats-skeleton-card-${i}`} className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 animate-pulse">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="h-3 bg-gray-200 rounded w-24 mb-2"></div>
                                    <div className="h-8 bg-gray-200 rounded w-16"></div>
                                </div>
                                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                            </div>
                            <div className="h-3 bg-gray-200 rounded w-32"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div id="content-creator-dashboard-error" className="min-h-screen bg-white p-4 mt-12 md:p-6 lg:p-8">
                <div className="mb-6 md:mb-8">
                    <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                        {t('Content Creator Dashboard')}
                    </h1>
                    <p className="text-xs md:text-sm text-gray-600">
                        {t('Manage your educational content and track performance')}
                    </p>
                </div>

                <div id="dashboard-error-banner" className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                    <div className="text-red-500 mb-2">
                        <ClipboardList className="w-12 h-12 mx-auto" />
                    </div>
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                        {t('Error Loading Dashboard')}
                    </h3>
                    <p id="dashboard-error-message" className="text-sm text-red-700 mb-4">{error}</p>
                    <button
                        id="btn-retry-dashboard"
                        onClick={fetchDashboardData}
                        className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                    >
                        {t('Retry')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div id="content-creator-dashboard" className="min-h-screen bg-white p-4 mt-12 md:p-6 lg:p-8">
            {/* Header Section */}
            <div id="dashboard-header" className="mb-6 md:mb-8">
                <h1 id="dashboard-title" className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                    {t('Content Creator Dashboard')}
                </h1>
                <p id="dashboard-subtitle" className="text-xs md:text-sm text-gray-600">
                    {t('Manage your educational content and track performance')}
                </p>
            </div>

            {/* Stats Grid */}
            <div id="stats-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
                {stats.map((stat, index) => (
                    <div
                        key={index}
                        id={stat.id}
                        className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-300 p-4 md:p-6"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                                <p id={`${stat.id}-title`} className="text-xs text-gray-600 mb-1 font-medium">
                                    {t(stat.titleKey)}
                                </p>
                                <h3 id={`${stat.id}-value`} className="text-2xl md:text-3xl font-bold text-gray-900">
                                    {stat.value}
                                </h3>
                            </div>
                            <div className={`${stat.iconBg} p-3 rounded-lg`}>
                                <stat.icon className={`w-5 h-5 md:w-6 md:h-6 ${stat.iconColor}`} />
                            </div>
                        </div>
                        <p id={`${stat.id}-subtitle`} className="text-xs text-gray-500">
                            {stat.subtitle}
                        </p>
                    </div>
                ))}
            </div>

            {/* Quick Actions Section */}
            <div id="quick-actions-section" className="mb-6 md:mb-8">
                <h2 id="quick-actions-title" className="text-lg md:text-xl font-bold text-gray-900 mb-4">
                    {t('Quick Actions')}
                </h2>
                <div id="quick-actions-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {quickActions.map((action, index) => (
                        <button
                            key={index}
                            id={action.id}
                            onClick={() => navigateToSection(action.path)}
                            className={`${action.bgColor} ${action.hoverColor} text-white rounded-xl p-4 md:p-5 text-left transition-all duration-300 transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500`}
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <action.icon className="w-5 h-5 md:w-6 md:h-6" />
                                <h3 className="text-sm md:text-base font-bold">
                                    {t(action.titleKey)}
                                </h3>
                            </div>
                            <p className="text-xs opacity-90 ml-8 md:ml-9">
                                {t(action.subtitleKey)}
                            </p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Bottom Section */}
            <div id="dashboard-bottom-section" className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Recent Assessments */}
                <div id="recent-assessments-section" className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 h-[460px] flex flex-col">
                    <h2 id="recent-assessments-title" className="text-lg md:text-xl font-bold text-gray-900 mb-4 shrink-0">
                        {t('Recent Assessments')}
                    </h2>

                    {!dashboardData?.recentAssessments?.length ? (
                        <div id="recent-assessments-empty" className="flex flex-col items-center justify-center flex-1">
                            <div className="bg-gray-100 rounded-full p-4 mb-4">
                                <ClipboardList className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-600 mb-4 text-center">
                                {t('No assessments created yet')}
                            </p>
                            <button
                                id="btn-create-first-assessment"
                                onClick={() => navigateToSection('/app/assessmentCreate')}
                                className="bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800"
                            >
                                {t('Create Your First Assessment')}
                            </button>
                        </div>
                    ) : (
                        /* Scrollable list */
                        <div id="recent-assessments-list" className="space-y-4 overflow-y-auto pr-2 flex-1">
                            {dashboardData?.recentAssessments?.map((assessment) => (
                                <div
                                    key={assessment.id}
                                    id={`assessment-card-${assessment.id}`}
                                    onClick={() => navigateToSection(`/app/assessments`)}
                                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
                                >
                                    <div className="flex justify-between items-start gap-3 mb-1">

                                        {/* Title with tooltip */}
                                        <div className="relative group max-w-[75%]">
                                            <h3 id={`assessment-title-${assessment.id}`} className="font-semibold text-gray-900 text-sm line-clamp-1">
                                                {assessment.title}
                                            </h3>

                                            {/* Tooltip */}
                                            <div className="absolute left-0 top-full z-20 mt-1 hidden group-hover:block">
                                                <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 max-w-xs">
                                                    {assessment.title}
                                                </div>
                                            </div>
                                        </div>

                                        <span
                                            id={`assessment-status-${assessment.id}`}
                                            className={`px-2 py-1 rounded text-xs font-medium ${assessment.status_type === 1
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-yellow-100 text-yellow-700'
                                                }`}
                                        >
                                            {assessment.status_type === 1 ? t('Active') : t('Draft')}
                                        </span>
                                    </div>

                                    {assessment.short_description && (
                                        <div className="relative group">
                                            <p className="text-xs text-gray-600 line-clamp-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: assessment.short_description,
                                                }}
                                            />

                                            <div className="absolute left-0 top-full z-20 mt-1 hidden group-hover:block">
                                                <div
                                                    className="bg-gray-900 text-white text-xs rounded px-2 py-1 max-w-sm"
                                                    dangerouslySetInnerHTML={{
                                                        __html: assessment.short_description,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div id={`assessment-meta-${assessment.id}`} className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                                        <span>{assessment.type}</span>
                                        {assessment.complexity_level && (
                                            <>
                                                <span>•</span>
                                                <span>{assessment.complexity_level}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Question Distribution */}
                <div id="question-distribution-section" className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 h-[460px] flex flex-col">
                    <h2 id="question-distribution-title" className="text-lg md:text-xl font-bold text-gray-900 mb-6">
                        {t('Question Distribution')}
                    </h2>

                    {!dashboardData?.questionDistribution?.length ? (
                        <div id="question-distribution-empty" className="flex flex-col items-center justify-center flex-1">
                            <div className="bg-gray-100 rounded-full p-4 mb-4">
                                <HelpCircle className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-600 mb-4 text-center">
                                {t('No questions created yet')}
                            </p>
                            <button
                                id="btn-create-first-question"
                                onClick={() => navigateToSection('/app/questions')}
                                className="bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800"
                            >
                                {t('Create Your First Question')}
                            </button>
                        </div>
                    ) : (
                        /* Scrollable content */
                        <div id="question-distribution-list" className="space-y-4 overflow-y-auto pr-2 flex-1">
                            {dashboardData.questionDistribution
                                .filter((dist) => dist.level !== 'foundation')
                                .map((dist, index) => {
                                    const total = dashboardData.stats.questions.total;
                                    const percentage =
                                        total > 0 ? ((dist.count / total) * 100).toFixed(1) : 0;

                                    return (
                                        <div key={index} id={`question-dist-row-${dist.level}`} className="space-y-2">
                                            <div className="flex justify-between text-sm">
                                                <span id={`question-dist-level-${dist.level}`} className="font-medium text-gray-700">
                                                    {dist.level}
                                                </span>
                                                <span id={`question-dist-count-${dist.level}`} className="text-gray-600">
                                                    {dist.count} ({percentage}%)
                                                </span>
                                            </div>

                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    id={`question-dist-bar-${dist.level}`}
                                                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>


            </div>
        </div>
    );
};

export default ContentCreatorDashboard;